import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useCurrentUser } from "./use-current-user";

const onboardingContext = createContext();

export function useOnboardingContext() {
  return useContext(onboardingContext);
}

function useProvideOnboarding() {
  const [shouldShowFirstLoginTour, setShouldShowFirstLoginTour] =
    useState(null);

  return {
    shouldShowFirstLoginTour,
    setShouldShowFirstLoginTour,
  };
}

export function OnboardingProvider({ children }) {
  const onboarding = useProvideOnboarding();

  return (
    <onboardingContext.Provider value={onboarding}>
      {children}
    </onboardingContext.Provider>
  );
}

export function useOnboarding() {
  const { data, loading, refetch } = useCurrentUser();

  const navigate = useNavigate();
  const location = useLocation();

  const { shouldShowFirstLoginTour, setShouldShowFirstLoginTour } =
    useOnboardingContext();

  const user = data?.user;

  useEffect(() => {
    if (!loading && user) {
      const onboardingSkipped = localStorage.getItem(
        "onboardingSkipped",
      );

      // If onboarding is not complete and user hasn't skipped it, redirect to onboarding
      // But don't redirect if we're already on the onboarding page or just completed onboarding
      const isOnOnboardingPage =
        window.location.pathname.startsWith("/onboarding");
      const justCompletedOnboarding =
        window.location.pathname === "/dashboard" &&
        location?.state?.fromOnboarding;
      const isInPimsPalIntegration =
        window.location.pathname.startsWith("/pimspal");

      if (
        !user?.isOnboardingComplete &&
        !onboardingSkipped &&
        !isOnOnboardingPage &&
        !justCompletedOnboarding &&
        !isInPimsPalIntegration
      ) {
        navigate("/onboarding");
      } else {
        setShouldShowFirstLoginTour(false);
      }
    }
  }, [
    user,
    loading,
    navigate,
    location?.state?.fromOnboarding,
    setShouldShowFirstLoginTour,
  ]);

  const skipOnboarding = () => {
    localStorage.setItem("onboardingSkipped", "true");
  };

  return {
    isOnboardingComplete: user?.isOnboardingComplete,
    refetchUserOnboardingInfo: refetch,
    shouldShowFirstLoginTour,
    setShouldShowFirstLoginTour,
    skipOnboarding,
  };
}
